import { UserDocumentContract, UserDocumentUsage } from '@/types';
import { useStore } from '@/utils/vue';

const USER_DOCUMENT_CONTRACT_KEY = 'latestUserDocumentContract';
const IS_NOTIFICATION_SEEN_KEY = 'isUserDocumentNotificationSeen';

export function useUserDocumentLimitNotification() {
  const store = useStore();

  const refreshNotification = (
    isAdminUser: boolean,
    userDocumentUsage: UserDocumentUsage | undefined,
    activeUserDocumentContract: UserDocumentContract | undefined,
  ) => {
    // 使用量オーバー通知する条件：
    // - 管理者である（使用量情報を取得できる）
    // - 使用量制限に達している
    // - 表示された通知は未閲覧か、もしくは閲覧後に契約情報が変更されている
    const markedAsSeen =
      localStorage.getItem(IS_NOTIFICATION_SEEN_KEY) === 'true';
    const isContractChanged =
      JSON.stringify(activeUserDocumentContract) !==
      localStorage.getItem(USER_DOCUMENT_CONTRACT_KEY);
    const hasActiveContract = !!activeUserDocumentContract;
    const hasUsageExceededNotification =
      isAdminUser &&
      !!userDocumentUsage &&
      hasActiveContract &&
      userDocumentUsage.limit_reached &&
      (!markedAsSeen || isContractChanged);

    // 契約期限切れ通知する条件：
    // - 管理者である
    // - 旧契約が存在する
    // - 現在の有効契約が存在しない
    // - 表示された通知は未閲覧
    const hasOldContract =
      localStorage.getItem(USER_DOCUMENT_CONTRACT_KEY) !== null;
    const hasContractExpiredNotification =
      isAdminUser && hasOldContract && !hasActiveContract && !markedAsSeen;

    const hasNotification =
      hasUsageExceededNotification || hasContractExpiredNotification;
    const notificationType = hasUsageExceededNotification
      ? 'usage_exceeded'
      : hasContractExpiredNotification
        ? 'contract_expired'
        : undefined;

    // 契約が変わった場合、閲覧済みフラグをリセット
    if (hasActiveContract && isContractChanged && markedAsSeen) {
      localStorage.removeItem(IS_NOTIFICATION_SEEN_KEY);
    }

    // 再表示時の比較のため契約を保存
    if (activeUserDocumentContract !== undefined) {
      localStorage.setItem(
        USER_DOCUMENT_CONTRACT_KEY,
        JSON.stringify(activeUserDocumentContract),
      );
    }

    store.commit(
      'userDocumentNotification/setHasNotification',
      hasNotification,
    );
    store.commit(
      'userDocumentNotification/setNotificationType',
      notificationType,
    );
  };

  const markAsSeen = () => {
    // 閲覧済みフラグ
    localStorage.setItem(IS_NOTIFICATION_SEEN_KEY, 'true');

    // 通知を非表示
    store.commit('userDocumentNotification/setHasNotification', false);
    store.commit('userDocumentNotification/setNotificationType', undefined);
  };

  return {
    refreshNotification,
    markAsSeen,
  };
}
